import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatNumber, formatPercentage } from "@/utils/formatters";

export default function StyleGuide() {
  const colorSwatches = [
    { name: "Primary", value: "#1A9B8A", class: "bg-primary", description: "Main brand color" },
    { name: "Background", value: "#082029", class: "bg-background", description: "Page background" },
    { name: "Card", value: "var(--card)", class: "bg-card", description: "Card backgrounds" },
    { name: "Surface", value: "#1C1F33", class: "bg-surface", description: "Surface elements" },
    { name: "Muted", value: "var(--muted)", class: "bg-muted", description: "Subdued backgrounds" },
    { name: "Border", value: "var(--border)", class: "bg-border", description: "Border color" },
    { name: "Destructive", value: "var(--destructive)", class: "bg-destructive", description: "Error states" },
    { name: "Secondary", value: "var(--secondary)", class: "bg-secondary", description: "Secondary elements" },
  ];

  const buttonVariants = [
    { variant: "default" as const, label: "Default" },
    { variant: "outline" as const, label: "Outline" },
    { variant: "destructive" as const, label: "Destructive" },
    { variant: "ghost" as const, label: "Ghost" },
    { variant: "link" as const, label: "Link" },
    { variant: "secondary" as const, label: "Secondary" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* Print PDF Button */}
      <div className="flex justify-end px-8 pt-4 print:hidden">
        <button
          onClick={() => window.print()}
          className="bg-primary text-black text-sm font-semibold px-4 py-2 rounded-md shadow hover:opacity-90 transition-opacity"
        >
          Print PDF
        </button>
      </div>
      
      <div className="max-w-7xl mx-auto p-8 space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-white">Redwood Design System</h1>
          <p className="text-muted-foreground text-lg">
            Visual style guide for the R2R Value Platform
          </p>
        </div>

        {/* Typography Section */}
        <Card className="bg-card border-border rounded-2xl break-inside-avoid">
          <CardHeader>
            <CardTitle className="text-white">Typography</CardTitle>
            <CardDescription className="text-muted-foreground">
              Font families and text styles using Archivo and Roboto Mono
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Archivo Font Family */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary">Archivo (Primary Font)</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">.font-sans .text-4xl .font-bold</p>
                  <h1 className="text-4xl font-bold text-white font-sans">Heading 1 - Page Title</h1>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">.font-sans .text-2xl .font-semibold</p>
                  <h2 className="text-2xl font-semibold text-white font-sans">Heading 2 - Section Title</h2>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">.font-sans .text-lg .font-medium</p>
                  <h3 className="text-lg font-medium text-white font-sans">Heading 3 - Subsection</h3>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">.font-sans .text-base</p>
                  <p className="text-base text-white font-sans">
                    Body text using Archivo font family. This is the default text style for paragraphs and general content.
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">.font-sans .text-sm .text-muted-foreground</p>
                  <p className="text-sm text-muted-foreground font-sans">Small text and descriptions</p>
                </div>
              </div>
            </div>

            {/* Roboto Mono Font Family */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary">Roboto Mono (Code & Data)</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">.font-mono .text-sm</p>
                  <code className="font-mono text-sm bg-muted px-2 py-1 rounded text-white">
                    const projectId = "fcd9b037-d37a-4ebd"
                  </code>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">.font-mono Badge component</p>
                  <Badge variant="secondary" className="font-mono">
                    ROI: 185%
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Color Palette Section */}
        <Card className="bg-card border-border rounded-2xl break-inside-avoid">
          <CardHeader>
            <CardTitle className="text-white">Color Palette</CardTitle>
            <CardDescription className="text-muted-foreground">
              Primary colors and design tokens used throughout the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {colorSwatches.map((color) => (
                <div key={color.name} className="space-y-3">
                  <div className={`${color.class} w-full h-20 rounded-lg border border-border`}></div>
                  <div className="space-y-1">
                    <p className="font-medium text-white">{color.name}</p>
                    <p className="text-xs font-mono text-muted-foreground">{color.class}</p>
                    <p className="text-xs text-muted-foreground">{color.value}</p>
                    <p className="text-xs text-muted-foreground">{color.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Button Variants Section */}
        <Card className="bg-card border-border rounded-2xl break-inside-avoid">
          <CardHeader>
            <CardTitle className="text-white">Button Components</CardTitle>
            <CardDescription className="text-muted-foreground">
              All shadcn/ui button variants with consistent styling
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {buttonVariants.map((button) => (
                <div key={button.variant} className="space-y-2">
                  <Button variant={button.variant} className="w-full">
                    {button.label}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground font-mono">
                    variant="{button.variant}"
                  </p>
                </div>
              ))}
            </div>
            
            {/* Button Sizes */}
            <div className="mt-8 space-y-4">
              <h4 className="text-lg font-medium text-white">Button Sizes</h4>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="space-y-2">
                  <Button size="sm">Small</Button>
                  <p className="text-xs text-center text-muted-foreground font-mono">size="sm"</p>
                </div>
                <div className="space-y-2">
                  <Button size="default">Default</Button>
                  <p className="text-xs text-center text-muted-foreground font-mono">size="default"</p>
                </div>
                <div className="space-y-2">
                  <Button size="lg">Large</Button>
                  <p className="text-xs text-center text-muted-foreground font-mono">size="lg"</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card and Layout Components */}
        <Card className="bg-card border-border rounded-2xl break-inside-avoid">
          <CardHeader>
            <CardTitle className="text-white">Cards & Layout Tokens</CardTitle>
            <CardDescription className="text-muted-foreground">
              Layout primitives and spacing tokens used for consistent design
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            
            {/* Card Examples */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white">Card Components</h4>
              <div className="grid md:grid-cols-2 gap-6">
                
                {/* Basic Card */}
                <div className="space-y-2">
                  <Card className="bg-card border-border rounded-2xl">
                    <CardHeader>
                      <CardTitle className="text-white text-lg">Basic Card</CardTitle>
                      <CardDescription className="text-muted-foreground">
                        Standard card with header and content
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-white">Card content goes here with proper spacing and typography.</p>
                    </CardContent>
                  </Card>
                  <p className="text-xs text-muted-foreground font-mono">
                    .bg-card .border-border .rounded-2xl
                  </p>
                </div>

                {/* Metric Card */}
                <div className="space-y-2">
                  <Card className="bg-card border-border rounded-2xl">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-muted-foreground text-sm">Total ROI</p>
                          <p className="text-2xl font-bold text-primary">185%</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary" className="font-mono">
                            +12%
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <p className="text-xs text-muted-foreground font-mono">
                    Metric display card
                  </p>
                </div>

              </div>
            </div>

            {/* Layout Grid Examples */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white">Layout Grids</h4>
              
              {/* 2 Column Grid */}
              <div className="space-y-2">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-white text-sm">Column 1</p>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-white text-sm">Column 2</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground font-mono">
                  .grid .md:grid-cols-2 .gap-4
                </p>
              </div>

              {/* 3 Column Grid */}
              <div className="space-y-2">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-white text-sm">Column 1</p>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-white text-sm">Column 2</p>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-white text-sm">Column 3</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground font-mono">
                  .grid .md:grid-cols-3 .gap-6
                </p>
              </div>
            </div>

            {/* Spacing Examples */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white">Spacing Tokens</h4>
              <div className="space-y-4">
                
                <div className="space-y-2">
                  <div className="space-y-4 bg-muted p-4 rounded-lg">
                    <div className="bg-primary/20 p-2 rounded text-primary text-sm">Item 1</div>
                    <div className="bg-primary/20 p-2 rounded text-primary text-sm">Item 2</div>
                    <div className="bg-primary/20 p-2 rounded text-primary text-sm">Item 3</div>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">.space-y-4</p>
                </div>

                <div className="space-y-2">
                  <div className="flex gap-6 bg-muted p-4 rounded-lg">
                    <div className="bg-primary/20 p-2 rounded text-primary text-sm">Item 1</div>
                    <div className="bg-primary/20 p-2 rounded text-primary text-sm">Item 2</div>
                    <div className="bg-primary/20 p-2 rounded text-primary text-sm">Item 3</div>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">.flex .gap-6</p>
                </div>

              </div>
            </div>

          </CardContent>
        </Card>

        {/* Quadrant Chart Example */}
        <Card className="bg-card border-border rounded-2xl break-inside-avoid">
          <CardHeader>
            <CardTitle className="text-white">Chart Components</CardTitle>
            <CardDescription className="text-muted-foreground">
              Sample quadrant chart styling using SVG and design tokens
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              
              {/* Simple Quadrant Grid */}
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-1 bg-muted p-6 rounded-lg max-w-md mx-auto">
                  <div className="bg-card border border-border p-4 rounded text-center">
                    <p className="text-xs text-muted-foreground mb-2">High Impact</p>
                    <p className="text-xs text-muted-foreground">Low Effort</p>
                    <div className="mt-2 w-2 h-2 bg-primary rounded-full mx-auto"></div>
                  </div>
                  <div className="bg-card border border-border p-4 rounded text-center">
                    <p className="text-xs text-muted-foreground mb-2">High Impact</p>
                    <p className="text-xs text-muted-foreground">High Effort</p>
                    <div className="mt-2 w-2 h-2 bg-destructive rounded-full mx-auto"></div>
                  </div>
                  <div className="bg-card border border-border p-4 rounded text-center">
                    <p className="text-xs text-muted-foreground mb-2">Low Impact</p>
                    <p className="text-xs text-muted-foreground">Low Effort</p>
                    <div className="mt-2 w-2 h-2 bg-secondary rounded-full mx-auto"></div>
                  </div>
                  <div className="bg-card border border-border p-4 rounded text-center">
                    <p className="text-xs text-muted-foreground mb-2">Low Impact</p>
                    <p className="text-xs text-muted-foreground">High Effort</p>
                    <div className="mt-2 w-2 h-2 bg-muted rounded-full mx-auto"></div>
                  </div>
                </div>
                <p className="text-xs text-center text-muted-foreground font-mono">
                  .grid .grid-cols-2 with .bg-card .border-border
                </p>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Currency & Number Formatting Section */}
        <Card className="bg-card border-border rounded-2xl break-inside-avoid">
          <CardHeader>
            <CardTitle className="text-white">Currency & Number Display</CardTitle>
            <CardDescription className="text-muted-foreground">
              Standardized formatting for all numeric values across the platform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Formatting Functions */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white">Formatting Functions</h4>
              <div className="grid md:grid-cols-2 gap-6">
                
                <div className="space-y-3">
                  <h5 className="font-medium text-primary">Currency Formatting</h5>
                  <div className="bg-muted p-4 rounded-lg space-y-2">
                    <code className="text-sm font-mono text-white block">
                      import {`{formatCurrency}`} from '@/utils/formatters'
                    </code>
                    <div className="text-sm text-muted-foreground">Examples:</div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <code className="text-xs font-mono text-gray-300">formatCurrency(120000)</code>
                        <span className="right-num text-primary">{formatCurrency(120000)}</span>
                      </div>
                      <div className="flex justify-between">
                        <code className="text-xs font-mono text-gray-300">formatCurrency(1500000)</code>
                        <span className="right-num text-primary">{formatCurrency(1500000)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h5 className="font-medium text-primary">Number Formatting</h5>
                  <div className="bg-muted p-4 rounded-lg space-y-2">
                    <code className="text-sm font-mono text-white block">
                      import {`{formatNumber}`} from '@/utils/formatters'
                    </code>
                    <div className="text-sm text-muted-foreground">Examples:</div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <code className="text-xs font-mono text-gray-300">formatNumber(2500)</code>
                        <span className="right-num text-primary">{formatNumber(2500)}</span>
                      </div>
                      <div className="flex justify-between">
                        <code className="text-xs font-mono text-gray-300">formatNumber(1000000)</code>
                        <span className="right-num text-primary">{formatNumber(1000000)}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Styling Guidelines */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white">Numeric Display Styling</h4>
              
              <div className="bg-muted p-4 rounded-lg space-y-3">
                <div className="space-y-2">
                  <h5 className="font-medium text-white">Right-Aligned Numbers</h5>
                  <code className="text-sm font-mono text-primary block">
                    className="right-num"
                  </code>
                  <p className="text-sm text-muted-foreground">
                    Use for table cells, KPI values, and numeric spans
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">Example Usage:</div>
                  <code className="text-xs font-mono text-gray-300 block">
                    {`<td className="right-num">{formatCurrency(120000)}</td>`}
                  </code>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">Demo Table:</div>
                  <div className="border border-border rounded overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-background">
                        <tr>
                          <th className="text-left p-2 text-white text-sm">Metric</th>
                          <th className="text-right p-2 text-white text-sm">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-border">
                          <td className="p-2 text-sm text-muted-foreground">Annual Savings</td>
                          <td className="p-2 right-num text-primary">{formatCurrency(850000)}</td>
                        </tr>
                        <tr className="border-t border-border">
                          <td className="p-2 text-sm text-muted-foreground">Total FTEs</td>
                          <td className="p-2 right-num text-primary">{formatNumber(260)}</td>
                        </tr>
                        <tr className="border-t border-border">
                          <td className="p-2 text-sm text-muted-foreground">ROI Percentage</td>
                          <td className="p-2 right-num text-primary">{formatPercentage(185.5)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Design Tokens Summary */}
        <Card className="bg-card border-border rounded-2xl break-inside-avoid">
          <CardHeader>
            <CardTitle className="text-white">Design Tokens Reference</CardTitle>
            <CardDescription className="text-muted-foreground">
              Quick reference for key design system tokens
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              <div className="space-y-2">
                <h4 className="font-medium text-white">Typography</h4>
                <div className="text-sm space-y-1 text-muted-foreground font-mono">
                  <p>.font-sans (Archivo)</p>
                  <p>.font-mono (Roboto Mono)</p>
                  <p>.text-4xl .text-2xl .text-lg</p>
                  <p>.font-bold .font-semibold</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-white">Colors</h4>
                <div className="text-sm space-y-1 text-muted-foreground font-mono">
                  <p>.text-primary (#34E5E2)</p>
                  <p>.bg-card .bg-background</p>
                  <p>.text-muted-foreground</p>
                  <p>.border-border</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-white">Numbers & Currency</h4>
                <div className="text-sm space-y-1 text-muted-foreground font-mono">
                  <p>formatCurrency() formatNumber()</p>
                  <p>.right-num (alignment)</p>
                  <p>font-variant-numeric: tabular-nums</p>
                  <p>Always use standard formatters</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-white">Spacing</h4>
                <div className="text-sm space-y-1 text-muted-foreground font-mono">
                  <p>.space-y-4 .space-y-6</p>
                  <p>.gap-4 .gap-6 .gap-8</p>
                  <p>.p-4 .p-6 .pt-6</p>
                  <p>.rounded-2xl .rounded-lg</p>
                </div>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center pt-8 border-t border-border">
          <p className="text-muted-foreground text-sm">
            Redwood R2R Value Platform • Design System v1.0
          </p>
        </div>

      </div>
    </div>
  );
}